import type { Plan, Ridden, Suggestion } from '@painpal/contracts';
import { addDays } from '@painpal/core';
import { Link } from '@tanstack/react-router';
import { useRef, useState, type ReactNode } from 'react';
import { useSavePlanDay } from '../../api/mutations';
import { usePlan, useRecommendations } from '../../api/queries';
import { errorMessage } from '../../lib/api';
import { formatHoursMinutes, formatWeekdayDayMonth, formatWhole } from '../../lib/format';
import { useIsPhone } from '../../ui/useMediaQuery';
import { PlanTag } from '../calendar/PlanTag';
import { formatEstimate, nextLine, planDuration, todayPlanView, type NextBlock, type TodayPlanView } from '../calendar/planModel';
import {
  outcomeText, recommendationOn, suggestionDuration, suggestionInput, whyLine, type ActiveRecommendation,
} from '../recommendations/recommendationModel';
import { SwapStrip } from '../recommendations/SwapStrip';
import { useDismissals } from '../recommendations/useDismissals';
import styles from './todaysPlan.module.css';

const Rule = ({ tall = false }: { tall?: boolean }) => <span className={tall ? styles.tallRule : styles.rule} aria-hidden="true" />;
const CalendarLink = () => <Link to="/calendar" className={styles.calendarLink}>CALENDAR ›</Link>;

function StripEyebrow({ today, done, suggested = false }: { today: string; done: boolean; suggested?: boolean }) {
  return (
    <p className={styles.eyebrow}>
      TODAY&apos;S PLAN <span className={styles.date}>· {formatWeekdayDayMonth(today)}</span>
      {done && <span className={styles.badge}><span className={styles.dot} aria-hidden="true" />DONE</span>}
      {suggested && <span className={styles.suggestedBadge}>SUGGESTED</span>}
    </p>
  );
}

function Block({ plan, note, children }: { plan: Plan; note: boolean; children?: ReactNode }) {
  const duration = planDuration(plan);
  return (
    <div className={styles.block}>
      <PlanTag type={plan.type} size="strip" />
      {duration !== null && <span className={styles.duration}>{duration.text}{duration.defaulted && <span className={styles.defaulted}> (DEFAULT)</span>}</span>}
      {note && plan.note !== null && <><Rule /><span className={styles.note}>{plan.note}</span></>}
      {children}
    </div>
  );
}

function Estimate({ load, quiet }: { load: number; quiet: boolean }) {
  return <p className={styles.estimate} data-quiet={quiet || undefined}><span className={styles.figure}>{formatEstimate(load)}</span> <span className={styles.unit}>LOAD</span></p>;
}

function RiddenFigures({ ridden }: { ridden: Ridden }) {
  return (
    <div className={styles.ridden}>
      <span className={styles.label}>{ridden.ride === null ? 'RIDDEN' : `RIDDEN · ${ridden.ride.name.toUpperCase()}`}</span>
      <p className={styles.riddenValues}>
        <span className={styles.figure}>{formatHoursMinutes(ridden.movingS)}</span> <span className={styles.unit} data-part="time">TIME</span>
        <span className={styles.sep} aria-hidden="true">·</span> <span className={styles.figure}>{formatWhole(ridden.load)}</span> <span className={styles.unit}>LOAD</span>
      </p>
    </div>
  );
}

// The board's VIEW RIDE, to the day's first ride; the calendar when there's no ride to link.
function RideLink({ ridden }: { ridden: Ridden }) {
  if (ridden.ride === null) return <CalendarLink />;
  return <Link to="/activities/$activityId" params={{ activityId: ridden.ride.id }} className={styles.calendarLink}>VIEW RIDE ›</Link>;
}

function NextUp({ next, rest }: { next: NextBlock | null; rest: boolean }) {
  if (next === null) return null;
  return (
    <>
      {rest
        ? <p className={styles.nextLine}>NEXT · {nextLine(next)}</p>
        : <div className={styles.next}><span className={styles.label}>UP NEXT</span><span className={styles.nextText}>{nextLine(next)}</span></div>}
      <Rule tall />
    </>
  );
}

function NothingPlanned({ today }: { today: string }) {
  return (
    <>
      <div className={styles.left}>
        <StripEyebrow today={today} done={false} />
        <p className={styles.nothing}>Nothing planned for today.<span className={styles.label}>ADD A BLOCK, OR PLAN THE WHOLE WEEK</span></p>
      </div>
      <Link to="/calendar" className={styles.planWeek}>PLAN YOUR WEEK ›</Link>
    </>
  );
}

function DesktopStrip({ today, plan, ridden, next }: TodayPlanView & { today: string; plan: Plan }) {
  return (
    <>
      <div className={styles.left}>
        <StripEyebrow today={today} done={ridden !== null} />
        <Block plan={plan} note />
      </div>
      <div className={styles.right}>
        {ridden === null
          ? <><NextUp next={next} rest={plan.type === 'rest'} /><Estimate load={plan.estimatedLoad} quiet={plan.type === 'rest'} /></>
          : <><div className={styles.plannedQuiet}><span className={styles.label}>PLANNED</span><Estimate load={plan.estimatedLoad} quiet /></div><Rule tall /><RiddenFigures ridden={ridden} /></>}
        <Rule tall />
        {ridden === null ? <CalendarLink /> : <RideLink ridden={ridden} />}
      </div>
    </>
  );
}

function PhoneStrip({ today, plan, ridden, next }: TodayPlanView & { today: string; plan: Plan }) {
  return (
    <>
      <div className={styles.top}><StripEyebrow today={today} done={ridden !== null} /><CalendarLink /></div>
      <Block plan={plan} note={false}><span className={styles.spacer} /><Estimate load={plan.estimatedLoad} quiet={ridden !== null || plan.type === 'rest'} /></Block>
      {plan.note !== null && <p className={styles.note}>{plan.note}</p>}
      {ridden !== null && <RiddenFigures ridden={ridden} />}
      {plan.type === 'rest' && next !== null && <p className={styles.nextLine}>NEXT · {nextLine(next)}</p>}
    </>
  );
}

interface SuggestedProps { today: string; suggestion: Suggestion; saving: boolean; onAdd: () => void; onDismiss: () => void }

// Boards ncSSj / sOHDy: nothing is planned, so the rules' block shows in outline with grey figures until it's added.
function SuggestedCard({ today, suggestion, saving, onAdd, onDismiss }: SuggestedProps) {
  return (
    <>
      <div className={styles.left}>
        <StripEyebrow today={today} done={false} suggested />
        <div className={styles.block}>
          <PlanTag type={suggestion.type} size="strip" outline />
          <span className={styles.duration} data-quiet="true">{suggestionDuration(suggestion)}</span>
          <Estimate load={suggestion.estimatedLoad} quiet />
        </div>
        <p className={styles.structure}>{suggestion.structure}</p>
        <p className={styles.why}>{whyLine(suggestion.reasons)}</p>
      </div>
      <div className={styles.actions}>
        <button type="button" className={styles.notToday} onClick={onDismiss}>NOT TODAY</button>
        <button type="button" className={styles.add} disabled={saving} onClick={onAdd}>ADD TO PLAN</button>
      </div>
    </>
  );
}

interface BodyProps {
  today: string; isPhone: boolean; view: TodayPlanView; active: ActiveRecommendation | null;
  onDismiss: (basis: string, outcome: string) => void; onSaved: (outcome: string) => void;
}

function TodayBody({ today, isPhone, view, active, onDismiss, onSaved }: BodyProps) {
  const save = useSavePlanDay();
  const add = (suggestion: Suggestion, outcome: string): void =>
    save.mutate({ date: today, input: suggestionInput(suggestion) }, { onSuccess: () => onSaved(outcome) });
  const basis = active?.basis ?? '';
  const suggestion = active?.suggestion ?? null;
  const conflict = active?.conflict ?? null;
  const failed = save.error !== null && <p role="alert" className="alert-hot">{errorMessage(save.error)}</p>;
  if (view.plan === null) {
    if (suggestion === null) return <NothingPlanned today={today} />;
    return (
      <>
        <SuggestedCard
          today={today} suggestion={suggestion} saving={save.isPending}
          onAdd={() => add(suggestion, outcomeText({ kind: 'added', suggestion }))}
          onDismiss={() => onDismiss(basis, outcomeText({ kind: 'hidden' }))}
        />
        {failed}
      </>
    );
  }
  const Strip = isPhone ? PhoneStrip : DesktopStrip;
  return (
    <>
      <Strip today={today} {...view} plan={view.plan} />
      {/* Once SWAP lands, the plan can refetch before the recommendations do; the swap is done, so don't offer it again. */}
      {conflict !== null && conflict.alternative.type !== view.plan.type && (
        <SwapStrip
          conflict={conflict} saving={save.isPending}
          onKeep={() => onDismiss(basis, outcomeText({ kind: 'kept' }))}
          onSwap={() => add(conflict.alternative, outcomeText({ kind: 'swapped', suggestion: conflict.alternative }))}
        />
      )}
      {failed}
    </>
  );
}

// Shown whenever the Fitness page is, rides or not. Pending, failed or holding another range's placeholder, it stays out of the way: nothing else depends on it.
// The read starts a day early because the profile zone's today, which the strip shows, can be a day either side of the browser's.
// Recommendations that haven't arrived (or failed) just leave the strip as it would be without them.
// Adding or dismissing removes the focused button, so focus moves to the strip rather than dropping to the page,
// and the outcome is announced, since the strip's name alone doesn't say what changed.
// Each outcome gets a fresh node (keyed on a count), so the same words twice are still announced twice.
export function TodaysPlanStrip({ today: browserToday }: { today: string }) {
  const section = useRef<HTMLElement>(null);
  const [outcome, setOutcome] = useState({ text: '', count: 0 });
  const settle = (next: string): void => {
    setOutcome((last) => ({ text: next, count: last.count + 1 }));
    section.current?.focus();
  };
  const isPhone = useIsPhone();
  const read = usePlan(addDays(browserToday, -1), addDays(browserToday, 7));
  const recommendations = useRecommendations();
  const dismissals = useDismissals(browserToday);
  if (!read.isSuccess || read.isPlaceholderData) return null;
  const { today } = read.data;
  const active = recommendationOn(recommendations.data, today, dismissals.isDismissed);
  return (
    <section ref={section} tabIndex={-1} className={styles.strip} aria-label="Today's plan">
      <TodayBody
        today={today} isPhone={isPhone} view={todayPlanView(read.data.days, today)} active={active} onSaved={settle}
        onDismiss={(basis, next) => {
          dismissals.dismiss(today, basis);
          settle(next);
        }}
      />
      <p className="sr-only" role="status" aria-live="polite"><span key={outcome.count}>{outcome.text}</span></p>
    </section>
  );
}
